import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Calculator,
  Truck,
  Clock,
  CheckCircle2,
  Send,
  Loader2,
  MapPin,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SillCalculator = () => {
  const [formData, setFormData] = useState({
    length: "",
    depth: "",
    quantity: 1,
    edge_type: "none",
    additions: [],
    delivery_option: "collection",
    delivery_postcode: "",
    material_id: "",
  });

  const [quote, setQuote] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [checkingShipping, setCheckingShipping] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderForm, setOrderForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    delivery_address: "",
    delivery_postcode: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [prevTotal, setPrevTotal] = useState(0);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const res = await axios.get(`${API}/materials`);
      setMaterials(res.data);
    } catch (error) {
      console.error("Failed to fetch materials:", error);
    }
  };

  useEffect(() => {
    if (formData.length && formData.depth && formData.quantity) {
      const timer = setTimeout(() => {
        calculateQuote();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData.length, formData.depth, formData.quantity, formData.edge_type, formData.additions, formData.delivery_option, formData.material_id, shippingInfo]);

  useEffect(() => {
    if (formData.delivery_postcode && formData.delivery_postcode.length >= 2) {
      const timer = setTimeout(() => {
        checkShipping(formData.delivery_postcode);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setShippingInfo(null);
    }
  }, [formData.delivery_postcode]);

  const checkShipping = async (postcode) => {
    setCheckingShipping(true);
    try {
      const response = await axios.post(`${API}/shipping/calculate`, { postcode });
      setShippingInfo(response.data);
      
      if (response.data.delivery_option === 'collection_only') {
        setFormData(prev => ({ ...prev, delivery_option: 'collection' }));
      } else if (response.data.delivery_option === 'local_delivery') {
        setFormData(prev => ({ ...prev, delivery_option: 'local_delivery' }));
      } else if (response.data.delivery_option === 'shipping') {
        setFormData(prev => ({ ...prev, delivery_option: 'shipping' }));
      }
    } catch (error) {
      console.error("Shipping check error:", error);
    } finally {
      setCheckingShipping(false);
    }
  };

  const calculateQuote = async () => {
    if (!formData.length || !formData.depth) return;

    setCalculating(true);
    try {
      const response = await axios.post(`${API}/calculate`, {
        length: parseFloat(formData.length),
        depth: parseFloat(formData.depth),
        quantity: parseInt(formData.quantity) || 1,
        edge_type: formData.edge_type,
        additions: formData.additions,
        delivery_option: formData.delivery_option,
        delivery_postcode: formData.delivery_postcode,
        product_type: "sill",
        material_id: formData.material_id || undefined,
      });

      if (quote) {
        setPrevTotal(quote.total_price);
      }
      setQuote(response.data);
    } catch (error) {
      console.error("Calculation error:", error);
    } finally {
      setCalculating(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdditionToggle = (addition) => {
    setFormData((prev) => ({
      ...prev,
      additions: prev.additions.includes(addition)
        ? prev.additions.filter((a) => a !== addition)
        : [...prev.additions, addition],
    }));
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!quote) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/orders`, {
        quote_id: quote.quote_id,
        ...orderForm,
      });
      toast.success("Order submitted successfully! We'll be in touch soon.");
      setShowOrderModal(false);
      setOrderForm({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        delivery_address: "",
        delivery_postcode: "",
        notes: "",
      });
    } catch (error) {
      toast.error("Failed to submit order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const edgeOptions = [
    { value: "none", label: "Standard (No Polish)" },
    { value: "polished_front", label: "Polished Front Edge" },
    { value: "polished_front_sides", label: "Polished Front + Sides" },
    { value: "all_polished", label: "All Edges Polished" },
  ];

  const additionOptions = [
    { value: "drip_groove", label: "Drip Groove" },
    { value: "radius_corners", label: "Radius Corners" },
    { value: "cutouts", label: "Cut-outs" },
  ];

  const getDeliveryOptions = () => {
    const options = [{ value: "collection", label: "Collection (Free)" }];
    
    if (shippingInfo) {
      if (shippingInfo.delivery_option === 'local_delivery') {
        options.push({ 
          value: "local_delivery", 
          label: `Local Delivery (£${shippingInfo.price.toFixed(2)})` 
        });
      } else if (shippingInfo.delivery_option === 'shipping') {
        options.push({ 
          value: "shipping", 
          label: `Courier Delivery (£${shippingInfo.price.toFixed(2)})` 
        });
      }
    } else {
      options.push({ value: "local_delivery", label: "Local Delivery" });
      options.push({ value: "courier", label: "Courier Delivery" });
    }
    
    return options;
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Navigation */}
      <nav className="nav-wrapper" data-testid="sill-calculator-nav">
        <div className="container-padding py-4 flex items-center justify-between max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0F3D2E] flex items-center justify-center">
              <span className="text-white font-semibold text-lg">S</span>
            </div>
            <span className="font-semibold text-lg tracking-tight">
              Stone Sills Direct
            </span>
          </Link>

          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0F3D2E]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Calculator Type Toggle */}
      <div className="bg-white border-b border-[#E7E5E4]">
        <div className="container-padding max-w-7xl mx-auto py-4">
          <div className="flex gap-4">
            <Link to="/calculator/sills">
              <Button className="btn-primary" data-testid="sills-tab-active">
                Window Sills
              </Button>
            </Link>
            <Link to="/calculator/shelving">
              <Button variant="outline" className="btn-secondary" data-testid="shelving-tab">
                Shelving
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-padding max-w-7xl mx-auto py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Input Panel */}
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="stone-card" data-testid="sill-calculator-inputs">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-[#0F3D2E] flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-medium">Window Sill Calculator</h1>
                  <p className="text-sm text-[#57534E]">
                    Enter dimensions for your window sill
                  </p>
                </div>
              </div>

              {/* Dimensions */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-[#57534E] mb-4">
                    Dimensions
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="input-label">Length (mm)</Label>
                      <Input
                        type="number"
                        placeholder="e.g. 1200"
                        value={formData.length}
                        onChange={(e) => handleInputChange("length", e.target.value)}
                        className="input-field"
                        data-testid="input-length"
                      />
                    </div>
                    <div>
                      <Label className="input-label">Depth (mm)</Label>
                      <Input
                        type="number"
                        placeholder="e.g. 220"
                        value={formData.depth}
                        onChange={(e) => handleInputChange("depth", e.target.value)}
                        className="input-field"
                        data-testid="input-depth"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="input-label">Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => handleInputChange("quantity", e.target.value)}
                    className="input-field"
                    data-testid="input-quantity"
                  />
                </div>

                {/* Stone Colour */}
                {materials.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-[#57534E] mb-4">
                      Stone Colour
                    </h3>
                    <Select
                      value={formData.material_id}
                      onValueChange={(value) => handleInputChange("material_id", value)}
                    >
                      <SelectTrigger className="input-field" data-testid="select-material">
                        <SelectValue placeholder="Select stone colour" />
                      </SelectTrigger>
                      <SelectContent>
                        {materials.map((mat) => (
                          <SelectItem key={mat.id} value={mat.id}>
                            <div className="flex items-center gap-2">
                              {mat.image_url ? (
                                <img src={`${process.env.REACT_APP_BACKEND_URL}${mat.image_url}`} alt={mat.name} className="w-5 h-5 rounded-full object-cover border border-gray-200 flex-shrink-0" />
                              ) : (
                                <span className="w-4 h-4 rounded-full border border-gray-200 inline-block flex-shrink-0" style={{ backgroundColor: mat.colour_hex }} />
                              )}
                              {mat.name} — £{mat.price_per_sqm.toFixed(2)}/m²
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Edge Type */}
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-[#57534E] mb-4">
                    Edge Finish
                  </h3>
                  <Select
                    value={formData.edge_type}
                    onValueChange={(value) => handleInputChange("edge_type", value)}
                  >
                    <SelectTrigger className="input-field" data-testid="select-edge-type">
                      <SelectValue placeholder="Select edge finish" />
                    </SelectTrigger>
                    <SelectContent>
                      {edgeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Additions */}
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-[#57534E] mb-4">
                    Additions
                  </h3>
                  <div className="space-y-3">
                    {additionOptions.map((option) => (
                      <div key={option.value} className="flex items-center gap-3">
                        <Checkbox
                          id={option.value}
                          checked={formData.additions.includes(option.value)}
                          onCheckedChange={() => handleAdditionToggle(option.value)}
                          data-testid={`checkbox-${option.value}`}
                        />
                        <Label htmlFor={option.value} className="text-sm cursor-pointer">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Delivery */}
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-[#57534E] mb-4">
                    Delivery
                  </h3>
                  
                  <div className="mb-4">
                    <Label className="input-label">Your Postcode</Label>
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="e.g. OX11 7HH"
                        value={formData.delivery_postcode}
                        onChange={(e) => handleInputChange("delivery_postcode", e.target.value.toUpperCase())}
                        className="input-field pr-10"
                        data-testid="input-postcode"
                      />
                      {checkingShipping && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#57534E]" />
                      )}
                    </div>
                    {shippingInfo && (
                      <p className="text-sm mt-2 text-[#57534E]">
                        {shippingInfo.delivery_option === 'collection_only' ? (
                          <span className="text-amber-600">
                            Delivery not available to this postcode. Collection only.
                          </span>
                        ) : (
                          <span className="text-[#0F3D2E]">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {shippingInfo.zone_name}: £{shippingInfo.price.toFixed(2)}
                            {shippingInfo.estimated_days > 0 && ` (${shippingInfo.estimated_days} day${shippingInfo.estimated_days > 1 ? 's' : ''})`}
                          </span>
                        )}
                      </p>
                    )}
                  </div>

                  <Select
                    value={formData.delivery_option}
                    onValueChange={(value) => handleInputChange("delivery_option", value)}
                  >
                    <SelectTrigger className="input-field" data-testid="select-delivery">
                      <SelectValue placeholder="Select delivery option" />
                    </SelectTrigger>
                    <SelectContent>
                      {getDeliveryOptions().map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Quote Panel */}
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="calculator-panel p-8 lg:sticky lg:top-24" data-testid="quote-panel">
              {!quote && !calculating ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-[#F5F5F4] mx-auto flex items-center justify-center mb-4">
                    <Calculator className="w-8 h-8 text-[#57534E]" />
                  </div>
                  <h3 className="text-xl font-medium mb-2">Enter Your Dimensions</h3>
                  <p className="text-[#57534E]">Your instant quote will appear here</p>
                </div>
              ) : (
                <>
                  <div className="text-center mb-8">
                    <div className="mb-2">
                      <span className="text-sm text-[#57534E] uppercase tracking-wide">Total Price</span>
                    </div>
                    <div className="price-display" data-testid="total-price">
                      £{calculating ? (
                        <Loader2 className="w-8 h-8 inline animate-spin" />
                      ) : (
                        <CountUp start={prevTotal} end={quote?.total_price || 0} duration={0.5} decimals={2} />
                      )}
                    </div>
                    <div className="text-[#57534E]">
                      inc. VAT (£{quote?.price_ex_vat?.toFixed(2) || "0.00"} + £{quote?.vat_amount?.toFixed(2) || "0.00"} VAT)
                    </div>
                  </div>

                  <div className="border-t border-[#E7E5E4] pt-6 mb-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#0F3D2E]" />
                        <span className="text-[#57534E]">Lead Time:</span>
                        <span className="font-medium" data-testid="lead-time">{quote?.lead_time || 5} days</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#0F3D2E]" />
                        <span className="text-[#57534E]">Material:</span>
                        <span className="font-medium">{quote?.material || "Quartz"}</span>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <Truck className="w-4 h-4 text-[#0F3D2E]" />
                      <span className="text-sm text-[#57534E]">Edge Finish:</span>
                      <span className="text-sm font-medium">{quote?.edge_finish || "Standard"}</span>
                    </div>
                  </div>

                  {quote?.breakdown && (
                    <div className="bg-[#F9F9F7] p-4 mb-6" data-testid="price-breakdown">
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-[#57534E] mb-3">Price Breakdown</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-[#57534E]">Base Price</span>
                          <span>£{quote.breakdown.base_price?.toFixed(2)}</span>
                        </div>
                        {quote.breakdown.edge_cost > 0 && (
                          <div className="flex justify-between">
                            <span className="text-[#57534E]">Edge Polish</span>
                            <span>£{quote.breakdown.edge_cost?.toFixed(2)}</span>
                          </div>
                        )}
                        {quote.breakdown.additions_cost > 0 && (
                          <div className="flex justify-between">
                            <span className="text-[#57534E]">Additions</span>
                            <span>£{quote.breakdown.additions_cost?.toFixed(2)}</span>
                          </div>
                        )}
                        {quote.breakdown.delivery_cost > 0 && (
                          <div className="flex justify-between">
                            <span className="text-[#57534E]">Delivery</span>
                            <span>£{quote.breakdown.delivery_cost?.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {quote?.reference && (
                    <div className="text-center mb-6">
                      <span className="text-xs text-[#57534E] uppercase tracking-wide">Quote Reference</span>
                      <div className="font-mono text-lg" data-testid="quote-reference">{quote.reference}</div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <Button className="btn-primary w-full" onClick={() => setShowOrderModal(true)} data-testid="submit-order-btn">
                      <Send className="w-4 h-4 mr-2" />
                      Submit Order Request
                    </Button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Order Modal */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent className="sm:max-w-md" data-testid="order-modal">
          <DialogHeader>
            <DialogTitle>Submit Order Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitOrder} className="space-y-4">
            <div>
              <Label className="input-label">Your Name</Label>
              <Input required value={orderForm.customer_name} onChange={(e) => setOrderForm((prev) => ({ ...prev, customer_name: e.target.value }))} className="input-field" data-testid="order-name" />
            </div>
            <div>
              <Label className="input-label">Email Address</Label>
              <Input type="email" required value={orderForm.customer_email} onChange={(e) => setOrderForm((prev) => ({ ...prev, customer_email: e.target.value }))} className="input-field" data-testid="order-email" />
            </div>
            <div>
              <Label className="input-label">Phone Number</Label>
              <Input type="tel" required value={orderForm.customer_phone} onChange={(e) => setOrderForm((prev) => ({ ...prev, customer_phone: e.target.value }))} className="input-field" data-testid="order-phone" />
            </div>
            {formData.delivery_option !== 'collection' && (
              <>
                <div>
                  <Label className="input-label">Delivery Address</Label>
                  <Textarea required value={orderForm.delivery_address} onChange={(e) => setOrderForm((prev) => ({ ...prev, delivery_address: e.target.value }))} className="input-field min-h-[80px]" placeholder="Full delivery address" data-testid="order-address" />
                </div>
                <div>
                  <Label className="input-label">Delivery Postcode</Label>
                  <Input required value={orderForm.delivery_postcode || formData.delivery_postcode} onChange={(e) => setOrderForm((prev) => ({ ...prev, delivery_postcode: e.target.value.toUpperCase() }))} className="input-field" data-testid="order-postcode" />
                </div>
              </>
            )}
            <div>
              <Label className="input-label">Notes (Optional)</Label>
              <Textarea value={orderForm.notes} onChange={(e) => setOrderForm((prev) => ({ ...prev, notes: e.target.value }))} className="input-field min-h-[80px]" placeholder="Any additional details..." data-testid="order-notes" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="btn-secondary flex-1" onClick={() => setShowOrderModal(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary flex-1" disabled={submitting} data-testid="confirm-order-btn">
                {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>) : "Confirm Order"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SillCalculator;
